import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import LeadCaptureBox from "@/components/LeadCaptureBox";
import AiCampaignGeneratorModal from "@/components/AiCampaignGeneratorModal";
import { SITE_URL } from "@/lib/siteConfig";
import { billing, type PricingPlan } from "@/lib/api";
import {
  Zap, Shield, Target, BarChart3, GitBranch, Shuffle,
  Link as LinkIcon, Mail, Users, Check, ArrowRight,
} from "lucide-react";

const FEATURES = [
  { icon: Zap,      label: "AI Campaign Builder",    desc: "Write campaigns in seconds with AI trained on high-converting email copy.",             badge: null,          accent: "bg-coral/10 text-coral",    href: "/ai-campaign-builder"       },
  { icon: Target,   label: "Intent Lead Finder",     desc: "Scan Reddit for users expressing buying intent and auto-add them as subscribers.",       badge: "Unique",      accent: "bg-amber-50 text-amber-600", href: null },
  { icon: Shield,   label: "Email Validation",       desc: "Free MX + ZeroBounce validation on every subscriber. Invalid addresses never get sent.", badge: "vs MailBeast", accent: "bg-green-50 text-green-600", href: null },
  { icon: Users,    label: "Warmup Pool",            desc: "Cross-user seed network that builds real sender reputation — at zero extra cost.",        badge: "vs MailBeast", accent: "bg-blue-50 text-blue-600",   href: null },
  { icon: GitBranch,label: "Automation Flows",       desc: "Visual workflow builder with LinkedIn and SMS steps, conditional branching.",             badge: null,          accent: "bg-purple-50 text-purple-600", href: null },
  { icon: BarChart3,label: "Self-Optimizing",        desc: "Campaigns that rewrite themselves using open data. A/B test, follow up non-openers.",    badge: "Unique",      accent: "bg-teal/10 text-teal",       href: "/subject-line-optimisation" },
  { icon: Shuffle,  label: "Spintax",                desc: "{Hi|Hello|Hey} syntax randomises copy per recipient to dodge spam pattern detection.",    badge: "vs MailBeast", accent: "bg-coral/10 text-coral",    href: null },
  { icon: LinkIcon, label: "Custom Tracking Domains",desc: "Point your own subdomain at MoreMail's tracker. No shared-domain reputation penalties.",  badge: "vs MailBeast", accent: "bg-amber-50 text-amber-600", href: null },
  { icon: Mail,     label: "Deep Personalisation",   desc: "9 merge tokens including tech stack, funding, and recent company news.",                  badge: "Unique",      accent: "bg-green-50 text-green-600", href: null },
];

const COMPARE = [
  ["AI campaign writer",         true, true,  false, false],
  ["Email validation",           true, false, true,  false],
  ["Real warmup seed pool",      true, false, true,  false],
  ["Custom tracking domain",     true, false, true,  false],
  ["Spintax",                    true, false, true,  false],
  ["Intent-based lead finder",   true, false, false, false],
  ["LinkedIn + SMS automation",  true, false, false, false],
  ["Merchant partner network",   true, false, false, false],
  ["Deep subscriber enrichment", true, false, false, false],
  ["Free plan included",         true, true,  false, true ],
];

const TESTIMONIALS = [
  { quote: "Our open rates went from 18% to 41% in the first month. The AI writes better subject lines than I do.",         name: "Jamie Lin",   role: "Growth Lead, Pallet"    },
  { quote: "The lead finder alone is worth the subscription. It surfaces people already asking about our product on Reddit.", name: "Marcus Webb", role: "Founder, Fieldstack"    },
  { quote: "Finally a warmup system that actually works. Our IP reputation went from At Risk to Excellent in three weeks.",   name: "Sara Obi",    role: "Head of Email, Voltaire" },
];

function CheckMark({ yes }: { yes: boolean }) {
  return yes
    ? <div className="w-6 h-6 rounded-full bg-teal/10 flex items-center justify-center mx-auto"><Check size={13} className="text-teal" /></div>
    : <div className="w-5 h-0.5 bg-black/[0.08] mx-auto rounded" />;
}

export default function HomePage() {
  const [plans, setPlans] = useState<PricingPlan[]>([]);

  useEffect(() => {
    billing.plans()
      .then((d) => setPlans(d.plans.filter(p => p.is_active !== 0).sort((a, b) => a.sort_order - b.sort_order).slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <>
      <SeoHead
        title="MoreMail — AI Email Marketing That Actually Delivers"
        description="AI campaign builder, real warmup pool, email validation, intent lead finder, and spintax. All the features MailBeast charges extra for — included free."
        url={SITE_URL}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-paper via-paper to-coral/[0.06] pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16 sm:pb-20 text-center">

          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal/10 rounded-full text-teal text-xs font-bold mb-6 sm:mb-8">
            <span className="w-1.5 h-1.5 bg-teal rounded-full" />
            Now with email validation, custom tracking domains & spintax
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-[64px] font-bold leading-[1.08] tracking-tight mb-5 sm:mb-6">
            Send <em className="text-coral not-italic">smarter</em> emails.<br className="hidden sm:block" />
            <span className="sm:hidden"> </span>Get more opens.
          </h1>

          <p className="text-base sm:text-xl text-slate max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed">
            MoreMail combines AI copywriting, deliverability tools, intent-based lead capture,
            and self-optimizing campaigns to help you reach more inboxes and drive real results.
          </p>

          <div className="flex flex-col items-center gap-5">
            <AiCampaignGeneratorModal />
            <LeadCaptureBox />
            <Link to="/features"
              className="text-sm font-medium text-slate hover:text-ink transition-colors flex items-center gap-1">
              See all features <ArrowRight size={12} />
            </Link>
          </div>

          <div className="flex items-center justify-center gap-8 sm:gap-12 mt-10 sm:mt-14 flex-wrap">
            {[["42%","Avg open rate"],["12K+","Teams onboarded"],["$0","To get started"]].map(([v,l])=>(
              <div key={l} className="text-center">
                <div className="font-display text-2xl sm:text-3xl font-bold text-ink">{v}</div>
                <div className="text-xs text-slate font-semibold uppercase tracking-wider mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="bg-white border-y border-black/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-ink mb-3">Everything in one platform</h2>
            <p className="text-slate text-base sm:text-lg max-w-xl mx-auto">Features that other platforms charge hundreds extra for — all included.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {FEATURES.map((f) => {
              const card = (
                <div className={`group relative bg-paper rounded-2xl border border-black/[0.06] p-5 hover:border-black/[0.1] hover:shadow-md hover:shadow-black/[0.04] transition-all h-full ${f.href ? "cursor-pointer hover:border-coral/30" : ""}`}>
                  {f.badge && (
                    <span className={`absolute top-4 right-4 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      f.badge === "Unique" ? "bg-coral/10 text-coral" : "bg-amber-100 text-amber-700"
                    }`}>
                      {f.badge}
                    </span>
                  )}
                  <div className={`inline-flex p-2.5 rounded-xl mb-3 ${f.accent}`}>
                    <f.icon size={18} />
                  </div>
                  <h3 className={`font-bold text-ink mb-1.5 text-sm ${f.href ? "group-hover:text-coral transition-colors" : ""}`}>{f.label}</h3>
                  <p className="text-slate text-xs sm:text-sm leading-relaxed">{f.desc}</p>
                  {f.href && (
                    <p className="text-[11px] font-semibold text-coral mt-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      Learn more <ArrowRight size={10} />
                    </p>
                  )}
                </div>
              );
              return f.href
                ? <Link key={f.label} to={f.href} className="block">{card}</Link>
                : <div key={f.label}>{card}</div>;
            })}
          </div>
          <div className="text-center mt-10">
            <Link to="/features" className="inline-flex items-center gap-1.5 text-coral font-semibold text-sm hover:underline">
              Deep-dive on all features <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center mb-10 sm:mb-14">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-ink mb-3">How it works</h2>
          <p className="text-slate text-base">Three steps from zero to inbox.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 relative">
          <div className="hidden sm:block absolute top-8 left-[calc(33%-24px)] right-[calc(33%-24px)] h-px bg-gradient-to-r from-transparent via-black/[0.08] to-transparent" />
          {[
            { n:"01", title:"Import or capture leads", desc:"Import your list via CSV, add subscribers manually, or let the Reddit intent scanner find people already asking about what you sell." },
            { n:"02", title:"Build with AI",           desc:"The campaign wizard generates subject, body, and preview text. Add spintax for variation. Preview how it looks in Gmail, Outlook, and mobile." },
            { n:"03", title:"Send and self-optimize",  desc:"MoreMail tracks opens, triggers follow-up campaigns to non-openers, and rewrites future subject lines based on what's working." },
          ].map(({ n, title, desc }) => (
            <div key={n} className="flex flex-col items-center text-center sm:items-start sm:text-left gap-4">
              <div className="w-12 h-12 rounded-2xl bg-ink flex items-center justify-center text-coral font-display font-bold text-sm flex-shrink-0">
                {n}
              </div>
              <div>
                <h3 className="font-bold text-ink mb-2">{title}</h3>
                <p className="text-slate text-sm leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-white border-y border-black/[0.06]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-ink mb-3">Built to win</h2>
            <p className="text-slate text-base">Features that matter for deliverability and conversions.</p>
          </div>
          <div className="bg-paper rounded-2xl border border-black/[0.06] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[480px]">
                <thead>
                  <tr className="border-b border-black/[0.06]">
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate w-[44%]">Feature</th>
                    {[["MoreMail","coral"],["Mailchimp","slate"],["MailBeast","slate"],["ActiveCampaign","slate"]].map(([n,c])=>(
                      <th key={n} className={`px-3 py-4 text-center text-xs font-bold uppercase tracking-wider ${c === "coral" ? "text-coral" : "text-slate/60"}`}>
                        {n}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map(([label,...rest], i)=>(
                    <tr key={label as string} className={i % 2 === 0 ? "bg-white" : ""}>
                      <td className="px-5 py-3.5 text-sm text-ink font-medium">{label}</td>
                      {(rest as boolean[]).map((v, j) => (
                        <td key={j} className="px-3 py-3.5"><CheckMark yes={v} /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center mb-10 sm:mb-14">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-ink mb-3">Teams love it</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="bg-white rounded-2xl border border-black/[0.06] p-6 flex flex-col gap-4">
              <blockquote className="text-ink text-sm leading-relaxed flex-1">"{t.quote}"</blockquote>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-coral/10 border border-coral/20 flex items-center justify-center text-coral text-xs font-bold flex-shrink-0">
                  {t.name.split(" ").map(n=>n[0]).join("")}
                </div>
                <div>
                  <p className="text-sm font-semibold text-ink">{t.name}</p>
                  <p className="text-xs text-slate">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="bg-white border-y border-black/[0.06]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-ink mb-3">Simple pricing</h2>
          <p className="text-slate text-base mb-10">All features included on every plan.</p>
          {plans.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {plans.map((p) => {
                const isPopular = !!p.is_popular;
                const priceLabel = p.monthly_price === null ? "Custom" : p.monthly_price === 0 ? "$0" : `$${p.monthly_price % 1 === 0 ? Math.round(p.monthly_price) : p.monthly_price}`;
                const limitLabel = p.subscriber_limit ? `${(p.subscriber_limit >= 1000 ? `${p.subscriber_limit / 1000}K` : p.subscriber_limit)} subscribers` : "Unlimited";
                return (
                  <div key={p.slug} className={`relative rounded-2xl p-6 text-left ${isPopular ? "bg-ink text-white shadow-xl" : "bg-paper border border-black/[0.06]"}`}>
                    {isPopular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold px-3 py-1 bg-coral text-white rounded-full uppercase tracking-wide">Most popular</span>
                    )}
                    <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${isPopular ? "text-white/50" : "text-slate"}`}>{p.name}</p>
                    <div className={`font-display text-4xl font-bold mb-1 ${isPopular ? "text-white" : "text-ink"}`}>
                      {priceLabel}{p.monthly_price !== null && <span className={`text-base font-normal ml-1 ${isPopular ? "text-white/50" : "text-slate"}`}>/mo</span>}
                    </div>
                    <p className={`text-xs mb-4 ${isPopular ? "text-white/50" : "text-slate"}`}>{limitLabel}</p>
                    {p.description && (
                      <p className={`text-xs ${isPopular ? "text-white/70" : "text-slate"}`}>{p.description}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <Link to="/pricing" className="inline-flex items-center gap-1.5 text-coral font-semibold hover:underline">
            See full pricing and feature comparison <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-ink">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to send emails that convert?
          </h2>
          <p className="text-white/60 text-base mb-8">Free to start. No credit card. Cancel any time.</p>
          <div className="flex justify-center">
            <LeadCaptureBox />
          </div>
        </div>
      </section>
    </>
  );
}
